import React, { ReactElement, useEffect, useRef, useState } from "react";
import portraits, { defaultPortrait } from "../assets";
import { playSound } from "../effects/sound";
import { GameState } from "../types";
import { Award } from "./awards";
import "./AwardsCeremony.css";

/** How long the card waits ("goes to...") before the winner is named, and how long the winner stays up. */
export const SUSPENSE_MS = 1300;
export const SHOW_MS = 1500;

type Props = {
  awards: Award[];
  user: string;
  gameState: GameState;
  onDone: () => void;
};

/**
 * Before the results: each award is announced on its own. The card appears with "The Snake goes to...",
 * a short pause, then the winner is named. After the last award the results screen follows.
 */
export default function AwardsCeremony(props: Props): ReactElement {
  const { awards } = props;
  // The parent may pass a new callback on every render; the timers must not restart because of it.
  const onDone = useRef(props.onDone);
  onDone.current = props.onDone;
  const [index, setIndex] = useState(0);
  const [named, setNamed] = useState(false);

  useEffect(() => {
    setNamed(false);
    playSound("drumroll");
    const reveal = window.setTimeout(() => {
      setNamed(true);
      playSound("awardReveal");
    }, SUSPENSE_MS);
    const next = window.setTimeout(() => {
      if (index + 1 >= awards.length) {
        onDone.current();
      } else {
        setIndex(index + 1);
      }
    }, SUSPENSE_MS + SHOW_MS);
    return () => {
      window.clearTimeout(reveal);
      window.clearTimeout(next);
    };
  }, [index, awards.length]);

  const award = awards[index];
  return (
    <div className="ac-screen" role="dialog" aria-modal="true" aria-label="Awards">
      <div className="ac-kicker">
        AWARD {index + 1} OF {awards.length}
      </div>
      <div key={award.id} className={"ac-card" + (named ? " ac-named" : "")}>
        <div className="ac-icon" aria-hidden="true">
          {award.icon}
        </div>
        <div className="ac-title">{award.title}</div>
        <div className="ac-blurb">{award.blurb}</div>
      </div>
      <div className="ac-goes" aria-live="polite">
        {award.title} goes to
        {!named && (
          <span className="ac-dots" aria-hidden="true">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        )}
      </div>
      <div className="ac-winners">
        {named &&
          award.winners.map((name) => {
            const icon = props.gameState.icon[name];
            const portrait = icon && icon !== defaultPortrait ? portraits[icon] : undefined;
            return (
              <div key={name} className={"ac-winner" + (name === props.user ? " ac-you" : "")}>
                {portrait ? <img src={portrait} alt="" /> : <span className="ac-initial">{name.charAt(0)}</span>}
                <span className="ac-name">
                  {name}
                  {name === props.user ? " (you)" : ""}
                </span>
              </div>
            );
          })}
      </div>
      <button className="ac-skip" onClick={() => onDone.current()}>
        Skip
      </button>
    </div>
  );
}
